/**
 * RISK SUMMARY COMPONENT
 * 
 * Shows a summary of all documented risks
 * Displays counts by impact level and total contingency
 * 
 * Based on Document 5 specification
 */

import React, { useMemo } from 'react';
import { AlertTriangle, DollarSign, BarChart3 } from 'lucide-react';

const RiskSummary = ({ risks = [] }) => {
  const summary = useMemo(() => {
    const counts = {
      total: risks.length,
      high: risks.filter(r => r.impact_level === 'high').length,
      medium: risks.filter(r => r.impact_level === 'medium').length,
      low: risks.filter(r => r.impact_level === 'low').length,
    };
    
    const totalContingency = risks
      .filter(r => r.contingency_included && r.contingency_amount)
      .reduce((sum, r) => sum + (parseFloat(r.contingency_amount) || 0), 0);
    
    const risksByCategory = risks.reduce((acc, risk) => {
      acc[risk.category] = (acc[risk.category] || 0) + 1;
      return acc;
    }, {});
    
    return { counts, totalContingency, risksByCategory };
  }, [risks]);

  if (risks.length === 0) {
    return null;
  }

  return (
    <div className="p-4 bg-slate-800/50 border border-slate-700 rounded space-y-4">
      <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
        <BarChart3 className="w-4 h-4" />
        Risk Summary
      </h4>
      
      {/* Impact Level Counts */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">Total Risks:</span>
          <span className="font-semibold text-slate-200">{summary.counts.total}</span>
        </div>
        
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            <span className="text-red-400 font-medium">{summary.counts.high}</span>
            <span className="text-xs text-slate-500">High</span>
          </span>
          
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            <span className="text-amber-400 font-medium">{summary.counts.medium}</span>
            <span className="text-xs text-slate-500">Medium</span>
          </span>
          
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            <span className="text-emerald-400 font-medium">{summary.counts.low}</span>
            <span className="text-xs text-slate-500">Low</span>
          </span>
        </div>
      </div>

      {/* Total Contingency */}
      {summary.totalContingency > 0 && (
        <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded">
          <DollarSign className="w-5 h-5 text-emerald-400" />
          <div>
            <span className="text-sm text-slate-400">Total Contingency:</span>
            <span className="ml-2 text-lg font-bold text-emerald-400">
              ${summary.totalContingency.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* High Risk Warning */}
      {summary.counts.high > 0 && (
        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-sm font-medium text-red-400">
              {summary.counts.high} High Impact Risk{summary.counts.high > 1 ? 's' : ''}
            </span>
            <p className="text-xs text-slate-400 mt-0.5">
              High impact risks may significantly affect project scope, timeline, or budget.
              Ensure mitigation plans are in place.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskSummary;
